/**
 * Fleet Ops Store Contracts (SCH-2664)
 *
 * The payloads the backend's fleet-ops port sends the ops store (`apps/ops-store/convex/fleetRenewals.ts`,
 * `fleetProvisioning.ts` and `fleetPurge.ts`, routes in `http.ts`) under a `backend` credential holding
 * `fleetops:write`, and the store's answers. The store holds the browser-session renewal ledger and the
 * provisioning orchestrator's runs, steps and dispatches; every guarded transition is one Convex mutation
 * (plan KTD5), so each route here is one verb, not a generic write.
 *
 * Shapes follow the PlanetScale models they replace (`BrowserSessionRenewal`, `ProvisioningRun`,
 * `ProvisioningStep`, `ProvisioningDispatch` in `apps/backend/prisma/schema/devops.prisma`) field for field,
 * with three changes (plan KTD2, KTD3):
 * - identity is the public id (`renewalId` for a renewal) and parents link by public id string; a
 *   backfilled row also carries its PlanetScale numeric id as `legacyId`;
 * - runs, steps and dispatches carry `schoolId`, and `mobileAppPublicId` once an app exists;
 * - timestamps are ISO-8601 strings.
 *
 * Every object is `.strict()`, so a field the other side does not know is refused rather than dropped.
 * Free-form JSON (run inputs, step output and error, dispatch step states) is passed through whole: the
 * store keeps what the backend wrote, and the backend's content scan (plan KTD13) runs before the write.
 * Where a write needs a timestamp the store's own clock supplies it (`'now'`), so leases and sweeps never
 * compare two machines' clocks.
 *
 * Every route is `POST`, under `/fleet/…`; `FLEET_OPS_ROUTES` names them and `FLEET_OPS_ROUTE_CONTRACTS`
 * pairs each with its request and response schema, for the store's routes and the backend's port alike.
 *
 * Plan: docs/plans/2026-10-04-2353-refactor-fleet-workflow-state-to-ops-store-plan.md (U2).
 */

import { z } from 'zod'
import { BrowserStepStateSchema, ProvisioningDispatchStatusSchema } from '../../schemas/entities/provisioning-dispatch'
import { ProvisioningRunStatusSchema, ProvisioningStepStatusSchema } from '../../schemas/entities/provisioning'
import {
  RenewalCodeSourceSchema,
  RenewalFailOutcomeCodeSchema,
  RenewalIdSchema,
  RenewalModeSchema,
  RenewalRecordedCodeSchema,
  RenewalRefusalReasonSchema,
  RenewalStatusSchema,
  RenewalTriggerSchema
} from '../browser-session-renewals'

// =============================================================================
// Shared primitives
// =============================================================================

const IsoTimestampSchema = z.string().datetime()
/** A cuid the backend minted (Prisma's `@default(cuid())` today), or a runner-made id. */
const PublicIdSchema = z.string().min(1).max(64)
/** The PlanetScale `BigInt` primary key of a backfilled row; `null` on a row born in the store. */
const LegacyIdSchema = z.number().int().positive().nullable()
const SchoolIdSchema = z.string().min(1)
const SessionSlugSchema = z.string().min(1).max(64)
const DurationMsSchema = z.number().int().positive()
/** Ids one read or fetch may name. */
export const FLEET_OPS_MAX_IDS = 500
const IdListSchema = z.array(PublicIdSchema).max(FLEET_OPS_MAX_IDS)

/**
 * A timestamp a write sets: `'now'` stamps the store's clock, `null` clears it. Absent leaves it as it is.
 */
export const FleetTimestampWriteSchema = z.union([z.literal('now'), z.null()])
export type FleetTimestampWrite = z.infer<typeof FleetTimestampWriteSchema>

/** `{ type, message }`, kept with any extra keys a writer added. */
const FleetErrorSchema = z.object({ type: z.string(), message: z.string() }).passthrough()

/** The four fleet tables, as purge, digest and fetch name them. */
export const FleetOpsTableSchema = z.enum(['renewals', 'runs', 'steps', 'dispatches'])
export type FleetOpsTable = z.infer<typeof FleetOpsTableSchema>

// =============================================================================
// Documents
// =============================================================================

/**
 * One renewal (`BrowserSessionRenewal`). Platform-level: no school key, so the school purge never
 * touches it (KTD3). Holds no cookie value, password or code — there is no field for one.
 */
export const FleetRenewalDocumentSchema = z
  .object({
    renewalId: RenewalIdSchema,
    /** Present only on backfilled rows, so `verify` can name a `legacyId` mismatch. */
    legacyId: LegacyIdSchema.optional(),
    slug: SessionSlugSchema,
    sourceKey: z.string().min(1).max(191),
    trigger: RenewalTriggerSchema,
    mode: RenewalModeSchema,
    requestedByUserId: z.string().nullable(),
    dispatchId: z.string().nullable(),
    status: RenewalStatusSchema,
    /** `'held'` while the row holds the slug's lock (in flight or halted), `null` otherwise. */
    activeLock: z.literal('held').nullable(),
    leaseExpiresAt: IsoTimestampSchema,
    passwordSubmittedAt: IsoTimestampSchema.nullable(),
    smsRequestedAt: IsoTimestampSchema.nullable(),
    codeSource: RenewalCodeSourceSchema.nullable(),
    codeSubmittedByUserId: z.string().nullable(),
    codeChannel: z.string().nullable(),
    codeDeadlineAt: IsoTimestampSchema.nullable(),
    outcomeCode: RenewalRecordedCodeSchema.nullable(),
    /** Written by the backend from the code (`outcomeMessageFor`), never from runner text. */
    outcomeMessage: z.string().max(255).nullable(),
    twoFactorUsed: z.boolean(),
    trustExpiresAt: IsoTimestampSchema.nullable(),
    teamsCount: z.number().int().nullable(),
    admittedAt: IsoTimestampSchema.nullable(),
    observedSessionVersion: z.number().int().nullable(),
    claimedSessionVersion: z.number().int().nullable(),
    savedSessionVersion: z.number().int().nullable(),
    startedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.nullable(),
    haltResolvedAt: IsoTimestampSchema.nullable(),
    haltResolvedByUserId: z.string().nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema
  })
  .strict()
export type FleetRenewalDocument = z.infer<typeof FleetRenewalDocumentSchema>

/**
 * A run's persisted inputs: the SCRUBBED wizard inputs (`scrubInputs`), kept whole. `schoolId` is
 * required because the create mutation copies it onto the run and every step (KTD3), so a run that
 * fails before it gets an app is still purgeable.
 */
export const FleetProvisioningRunInputsSchema = z.object({ schoolId: SchoolIdSchema }).passthrough()
export type FleetProvisioningRunInputs = z.infer<typeof FleetProvisioningRunInputsSchema>

export const FleetProvisioningInitiatorSourceSchema = z.enum(['HUMAN', 'SYSTEM'])
export type FleetProvisioningInitiatorSource = z.infer<typeof FleetProvisioningInitiatorSourceSchema>

/** One `ProvisioningStep`. */
export const FleetProvisioningStepDocumentSchema = z
  .object({
    publicId: PublicIdSchema,
    legacyId: LegacyIdSchema,
    runPublicId: PublicIdSchema,
    schoolId: SchoolIdSchema,
    mobileAppPublicId: PublicIdSchema.nullable(),
    stepName: z.string().min(1).max(128),
    stepOrder: z.number().int(),
    status: ProvisioningStepStatusSchema,
    attemptCount: z.number().int().nonnegative(),
    output: z.unknown().nullable(),
    error: z.unknown().nullable(),
    startedAt: IsoTimestampSchema.nullable(),
    completedAt: IsoTimestampSchema.nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema
  })
  .strict()
export type FleetProvisioningStepDocument = z.infer<typeof FleetProvisioningStepDocumentSchema>

/** One `ProvisioningRun`. User and app links are bare public ids; a deleted user reads as unknown. */
export const FleetProvisioningRunDocumentSchema = z
  .object({
    publicId: PublicIdSchema,
    legacyId: LegacyIdSchema,
    schoolId: SchoolIdSchema,
    mobileAppPublicId: PublicIdSchema.nullable(),
    initiatedByUserPublicId: PublicIdSchema.nullable(),
    initiatorSource: FleetProvisioningInitiatorSourceSchema,
    status: ProvisioningRunStatusSchema,
    inputs: FleetProvisioningRunInputsSchema,
    error: z.unknown().nullable(),
    startedAt: IsoTimestampSchema.nullable(),
    completedAt: IsoTimestampSchema.nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema
  })
  .strict()
export type FleetProvisioningRunDocument = z.infer<typeof FleetProvisioningRunDocumentSchema>

/** A run with its steps in `stepOrder` — what every run read and run write answers with. */
export const FleetProvisioningRunWithStepsSchema = FleetProvisioningRunDocumentSchema.extend({
  steps: z.array(FleetProvisioningStepDocumentSchema)
}).strict()
export type FleetProvisioningRunWithSteps = z.infer<typeof FleetProvisioningRunWithStepsSchema>

/** A step's runner state, kept whole (the setup-graph run-all record keeps its options here). */
const FleetStepStatesSchema = z.record(z.string(), BrowserStepStateSchema.passthrough())

/**
 * One `ProvisioningDispatch`. Exactly one of `runPublicId` / `mobileAppPublicId` is set, as in
 * `DispatchRecord`: `mobileAppPublicId` marks an APP-SCOPED dispatch, and a run-scoped one reaches its
 * app through the run. `schoolId` is set on both, so the school purge finds every dispatch directly. The
 * one exception is an app-scoped dispatch of an app with no school (the shared School Journey app, whose
 * `MobileApp.school_id` is NULL): it holds `null`, and only the app purge reaches it.
 * `sessionSlug` is a plain string: the setup-graph records use `setup-graph`, which is no browser session.
 */
export const FleetProvisioningDispatchDocumentSchema = z
  .object({
    publicId: PublicIdSchema,
    legacyId: LegacyIdSchema,
    runPublicId: PublicIdSchema.nullable(),
    mobileAppPublicId: PublicIdSchema.nullable(),
    schoolId: SchoolIdSchema.nullable(),
    stepIds: z.array(z.string()),
    stepStates: FleetStepStatesSchema,
    sessionSlug: SessionSlugSchema,
    status: ProvisioningDispatchStatusSchema,
    /** Audit-only, never serialized to the API. */
    requestedByUserPublicId: PublicIdSchema.nullable(),
    error: FleetErrorSchema.nullable(),
    startedAt: IsoTimestampSchema.nullable(),
    completedAt: IsoTimestampSchema.nullable(),
    leaseExpiresAt: IsoTimestampSchema.nullable(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema
  })
  .strict()
  .refine(d => (d.runPublicId === null) !== (d.mobileAppPublicId === null), {
    message: 'exactly one of runPublicId and mobileAppPublicId is set'
  })
  .refine(d => d.schoolId !== null || d.runPublicId === null, {
    message: 'a run-scoped dispatch carries its run’s schoolId'
  })
export type FleetProvisioningDispatchDocument = z.infer<typeof FleetProvisioningDispatchDocumentSchema>

// =============================================================================
// Renewals
// =============================================================================

/**
 * The `apple_session_renewal` flag as the backend read it. The store applies it inside the mutation
 * that holds the lock; it never reads the flag itself (the flag lives in PlanetScale `SystemConfig`).
 */
export const FleetRenewalAdmissionSchema = z.discriminatedUnion('flag', [
  z.object({ flag: z.literal('unreadable') }).strict(),
  z.object({ flag: z.literal('disabled') }).strict(),
  z
    .object({
      flag: z.literal('enabled'),
      cooldownMinutes: z.number().int().nonnegative(),
      maxSignInsPerDay: z.number().int().nonnegative(),
      maxSmsPerDay: z.number().int().nonnegative()
    })
    .strict()
])
export type FleetRenewalAdmission = z.infer<typeof FleetRenewalAdmissionSchema>

/** The sentence for each code a mutation may write on its own (sweep endings, store-side refusals). */
export const FleetRenewalOutcomeMessagesSchema = z.partialRecord(RenewalRecordedCodeSchema, z.string().min(1).max(255))
export type FleetRenewalOutcomeMessages = z.infer<typeof FleetRenewalOutcomeMessagesSchema>

const OutcomeMessageSchema = z.string().min(1).max(255)

/** `POST /fleet/renewals/request`: a console REQUESTED row, holding no lock. */
export const FleetRenewalRequestRequestSchema = z
  .object({
    renewalId: RenewalIdSchema,
    slug: SessionSlugSchema,
    requestedMode: RenewalModeSchema,
    requestedByUserId: z.string().min(1),
    /** How long the row waits to be claimed; past it the next claim's sweep fails it. */
    claimWindowMs: DurationMsSchema
  })
  .strict()
export type FleetRenewalRequestRequest = z.infer<typeof FleetRenewalRequestRequestSchema>

/** `created: false` when the id already existed (a retried request); the row is answered as it stands. */
export const FleetRenewalRequestResponseSchema = z
  .object({ created: z.boolean(), renewal: FleetRenewalDocumentSchema })
  .strict()
export type FleetRenewalRequestResponse = z.infer<typeof FleetRenewalRequestResponseSchema>

/** `POST /fleet/renewals/fail-requested`: end a REQUESTED row whose job could not be enqueued. */
export const FleetRenewalFailRequestedRequestSchema = z
  .object({
    renewalId: RenewalIdSchema,
    outcomeCode: z.literal('enqueue-failed'),
    outcomeMessage: OutcomeMessageSchema
  })
  .strict()
export type FleetRenewalFailRequestedRequest = z.infer<typeof FleetRenewalFailRequestedRequestSchema>

/** `failed: false` when the row was not REQUESTED (a job claimed it) or does not exist. */
export const FleetRenewalFailRequestedResponseSchema = z.object({ failed: z.boolean() }).strict()
export type FleetRenewalFailRequestedResponse = z.infer<typeof FleetRenewalFailRequestedResponseSchema>

/**
 * `POST /fleet/renewals/claim`: the ledger half of a claim, in one mutation — the lazy sweep scoped to
 * the slug, the lock, and every admission check the ledger can answer (flag, cooldown, scheduled
 * interval, daily caps, dispatch in flight). The checks that need the stored session (revoked,
 * unreadable, already renewed) stay in the backend, which then calls `admit` or `release`.
 */
export const FleetRenewalClaimRequestSchema = z
  .object({
    slug: SessionSlugSchema,
    renewalId: RenewalIdSchema,
    trigger: RenewalTriggerSchema,
    /** `dispatch:<id>:<phase>`, `scheduled:<executionId>` or `console:<renewalId>` (KTD15 of the renewal plan). */
    sourceKey: z.string().min(1).max(191),
    /** The mode the trigger asked for; `AUTO` until admission resolves it. */
    requestedMode: RenewalModeSchema,
    dispatchId: z.string().min(1).max(64).nullable(),
    observedSessionVersion: z.number().int().nonnegative().nullable(),
    admission: FleetRenewalAdmissionSchema,
    /** For a DISPATCH claim, whether the backend found the dispatch in flight on this slug; `null` otherwise. */
    dispatchInFlight: z.boolean().nullable(),
    leaseMs: DurationMsSchema,
    scheduledMinIntervalMs: DurationMsSchema,
    outcomeMessages: FleetRenewalOutcomeMessagesSchema
  })
  .strict()
  .refine(
    r =>
      r.trigger === 'DISPATCH'
        ? r.dispatchId !== null && r.dispatchInFlight !== null
        : r.dispatchId === null && r.dispatchInFlight === null,
    {
      message: 'a DISPATCH claim carries dispatchId and dispatchInFlight; no other trigger does'
    }
  )
export type FleetRenewalClaimRequest = z.infer<typeof FleetRenewalClaimRequestSchema>

/** Another renewal holds the slug (`RenewalHolderSchema`, strict). */
export const FleetRenewalHolderSchema = z
  .object({
    renewalId: RenewalIdSchema,
    status: RenewalStatusSchema,
    leaseExpiresAt: IsoTimestampSchema,
    outcomeCode: RenewalRecordedCodeSchema.nullable()
  })
  .strict()
export type FleetRenewalHolder = z.infer<typeof FleetRenewalHolderSchema>

/**
 * The claim's answer. The kinds are `ClaimRenewalResponse`'s, and every kind but `claimed` has its exact
 * shape, so the backend passes those through unchanged. `claimed` carries the ledger row instead of the
 * jar (the jar never enters the store, R7):
 * - `admission: 'pending'`: this call took the lock; the backend runs its session checks, then `admit`
 *   or `release`.
 * - `admission: 'admitted'`: a retried claim under the caller's own renewal id that was already admitted;
 *   the backend re-serves it without re-running admission.
 *
 * The three lock clashes (R4) come back as distinct refusals: `renewal-id-mismatch` (the id is on the
 * ledger under another slug, trigger or source key), `source-key-reused` (another renewal used this
 * trigger), and `in-flight` / `halted` — or `lock-held` for a console request that lost the lock.
 */
export const FleetRenewalClaimResponseSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('claimed'),
      admission: z.enum(['pending', 'admitted']),
      renewal: FleetRenewalDocumentSchema,
      /** The latest SUCCEEDED row's trust expiry for this slug, for mode resolution; `null` when none. */
      latestTrustExpiresAt: IsoTimestampSchema.nullable()
    })
    .strict(),
  z.object({ kind: z.literal('in-flight'), holder: FleetRenewalHolderSchema }).strict(),
  z.object({ kind: z.literal('halted'), holder: FleetRenewalHolderSchema }).strict(),
  z
    .object({ kind: z.literal('already-renewed'), renewalId: RenewalIdSchema, savedSessionVersion: z.number().int() })
    .strict(),
  z
    .object({
      kind: z.literal('refused'),
      /** `null` when no row could be written (a reused source key). */
      renewalId: RenewalIdSchema.nullable(),
      reason: RenewalRefusalReasonSchema,
      cooldownUntil: IsoTimestampSchema.nullable().optional(),
      blockedByOutcomeCode: RenewalRecordedCodeSchema.nullable().optional()
    })
    .strict()
])
export type FleetRenewalClaimResponse = z.infer<typeof FleetRenewalClaimResponseSchema>

/** `POST /fleet/renewals/admit`: record the resolved mode and claimed version on a held, unadmitted row. */
export const FleetRenewalAdmitRequestSchema = z
  .object({
    renewalId: RenewalIdSchema,
    mode: RenewalModeSchema.exclude(['AUTO']),
    claimedSessionVersion: z.number().int().nullable()
  })
  .strict()
export type FleetRenewalAdmitRequest = z.infer<typeof FleetRenewalAdmitRequestSchema>

/** `admitted: false` when a concurrent retry admitted it first or the lease was taken over; `renewal` is the row now. */
export const FleetRenewalAdmitResponseSchema = z
  .object({ admitted: z.boolean(), renewal: FleetRenewalDocumentSchema.nullable() })
  .strict()
export type FleetRenewalAdmitResponse = z.infer<typeof FleetRenewalAdmitResponseSchema>

/** `POST /fleet/renewals/release`: end a held, still-CLAIMED row as REFUSED (`releaseAsRefused`). */
export const FleetRenewalReleaseRequestSchema = z
  .object({
    renewalId: RenewalIdSchema,
    reason: RenewalRefusalReasonSchema,
    outcomeMessage: OutcomeMessageSchema,
    /** For `already-renewed`: the version to reopen with, kept so a retried claim answers the same. */
    savedSessionVersion: z.number().int().nullable(),
    /** `true` from admission: release only a row no concurrent retry has admitted. */
    onlyUnadmitted: z.boolean()
  })
  .strict()
export type FleetRenewalReleaseRequest = z.infer<typeof FleetRenewalReleaseRequestSchema>

export const FleetRenewalReleaseResponseSchema = z
  .object({ released: z.boolean(), renewal: FleetRenewalDocumentSchema.nullable() })
  .strict()
export type FleetRenewalReleaseResponse = z.infer<typeof FleetRenewalReleaseResponseSchema>

/**
 * The answer to a guarded move on one renewal: it applied, or the row is not in a state the move may
 * leave from (`not-held` for runner reports, `not-halted` for resume, `not-awaiting-code` for an
 * operator code), or there is no such renewal.
 */
const transitionResponse = <Refusal extends string>(refusal: Refusal) =>
  z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('applied'), renewal: FleetRenewalDocumentSchema }).strict(),
    z.object({ kind: z.literal(refusal) }).strict(),
    z.object({ kind: z.literal('not-found') }).strict()
  ])

/** `POST /fleet/renewals/progress`: one runner progress step (`RenewalProgressRequestSchema`, strict). */
export const FleetRenewalProgressRequestSchema = z
  .object({
    renewalId: RenewalIdSchema,
    report: z.discriminatedUnion('step', [
      z.object({ step: z.literal('password-submitting') }).strict(),
      z
        .object({
          step: z.literal('awaiting-code'),
          codeChannel: z.enum(['sms', 'device', 'voice', 'unknown']),
          codeDeadlineAt: IsoTimestampSchema
        })
        .strict(),
      z.object({ step: z.literal('code-submitted'), codeSource: RenewalCodeSourceSchema }).strict()
    ])
  })
  .strict()
export type FleetRenewalProgressRequest = z.infer<typeof FleetRenewalProgressRequestSchema>

export const FleetRenewalProgressResponseSchema = transitionResponse('not-held')
export type FleetRenewalProgressResponse = z.infer<typeof FleetRenewalProgressResponseSchema>

/** `POST /fleet/renewals/authorize-sms`: check the SMS cap and stamp `smsRequestedAt` in one mutation. */
export const FleetRenewalAuthorizeSmsRequestSchema = z
  .object({ renewalId: RenewalIdSchema, admission: FleetRenewalAdmissionSchema })
  .strict()
export type FleetRenewalAuthorizeSmsRequest = z.infer<typeof FleetRenewalAuthorizeSmsRequestSchema>

export const FleetRenewalAuthorizeSmsResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('allowed'), smsRequestedAt: IsoTimestampSchema }).strict(),
  z
    .object({
      kind: z.literal('refused'),
      reason: z.enum(['capped-sms', 'sms-already-requested', 'flag-unreadable', 'disabled'])
    })
    .strict(),
  z.object({ kind: z.literal('not-held') }).strict(),
  z.object({ kind: z.literal('not-found') }).strict()
])
export type FleetRenewalAuthorizeSmsResponse = z.infer<typeof FleetRenewalAuthorizeSmsResponseSchema>

/**
 * `POST /fleet/renewals/operator-code`: record WHO typed a code on a row awaiting one. The code itself
 * stays in the backend's code store and never reaches this route.
 */
export const FleetRenewalOperatorCodeRequestSchema = z
  .object({ renewalId: RenewalIdSchema, submittedByUserId: z.string().min(1) })
  .strict()
export type FleetRenewalOperatorCodeRequest = z.infer<typeof FleetRenewalOperatorCodeRequestSchema>

export const FleetRenewalOperatorCodeResponseSchema = transitionResponse('not-awaiting-code')
export type FleetRenewalOperatorCodeResponse = z.infer<typeof FleetRenewalOperatorCodeResponseSchema>

/**
 * `POST /fleet/renewals/complete`: the ledger steps around the backend's session save, in order.
 * - `begin`: claim the completion (→ COMPLETING) and extend the lease, from CLAIMED, SIGNING_IN or
 *   AWAITING_CODE only, so a taken-over holder and a duplicate completion are both refused.
 * - `saved`: the save landed; COMPLETING → SUCCEEDED, lock released.
 * - `save-refused`: the save lost its compare-and-set; COMPLETING → FAILED with the backend code.
 * - `revert`: the save faulted with no verdict; COMPLETING → SIGNING_IN so the runner can retry.
 */
export const FleetRenewalCompleteRequestSchema = z.discriminatedUnion('step', [
  z.object({ step: z.literal('begin'), renewalId: RenewalIdSchema, leaseMs: DurationMsSchema }).strict(),
  z
    .object({
      step: z.literal('saved'),
      renewalId: RenewalIdSchema,
      twoFactorUsed: z.boolean(),
      smsRequested: z.boolean(),
      trustExpiresAt: IsoTimestampSchema.nullable(),
      teamsCount: z.number().int().nonnegative().nullable(),
      savedSessionVersion: z.number().int(),
      outcomeMessage: OutcomeMessageSchema
    })
    .strict(),
  z
    .object({
      step: z.literal('save-refused'),
      renewalId: RenewalIdSchema,
      outcomeCode: z.enum(['store-revoked', 'store-conflict']),
      outcomeMessage: OutcomeMessageSchema,
      smsRequested: z.boolean()
    })
    .strict(),
  z.object({ step: z.literal('revert'), renewalId: RenewalIdSchema }).strict()
])
export type FleetRenewalCompleteRequest = z.infer<typeof FleetRenewalCompleteRequestSchema>

export const FleetRenewalCompleteResponseSchema = transitionResponse('not-held')
export type FleetRenewalCompleteResponse = z.infer<typeof FleetRenewalCompleteResponseSchema>

/**
 * Whether a failure halts the lane. `on-repeat` halts when another renewal on the slug ended with the
 * same code inside `windowMs` (a second `code-rejected` within 24 hours), counted inside the mutation.
 */
export const FleetRenewalHaltPolicySchema = z.discriminatedUnion('policy', [
  z.object({ policy: z.literal('always') }).strict(),
  z.object({ policy: z.literal('never') }).strict(),
  z.object({ policy: z.literal('on-repeat'), windowMs: DurationMsSchema }).strict()
])
export type FleetRenewalHaltPolicy = z.infer<typeof FleetRenewalHaltPolicySchema>

/** `POST /fleet/renewals/fail`: FAILED (lock released) or HALTED (lock kept until resume). */
export const FleetRenewalFailRequestSchema = z
  .object({
    renewalId: RenewalIdSchema,
    outcomeCode: RenewalFailOutcomeCodeSchema,
    outcomeMessage: OutcomeMessageSchema,
    smsRequested: z.boolean(),
    halt: FleetRenewalHaltPolicySchema
  })
  .strict()
export type FleetRenewalFailRequest = z.infer<typeof FleetRenewalFailRequestSchema>

export const FleetRenewalFailResponseSchema = transitionResponse('not-held')
export type FleetRenewalFailResponse = z.infer<typeof FleetRenewalFailResponseSchema>

/** `POST /fleet/renewals/resume`: clear a HALTED row's lock; the row stays HALTED as history. */
export const FleetRenewalResumeRequestSchema = z
  .object({ renewalId: RenewalIdSchema, resolvedByUserId: z.string().min(1) })
  .strict()
export type FleetRenewalResumeRequest = z.infer<typeof FleetRenewalResumeRequestSchema>

export const FleetRenewalResumeResponseSchema = transitionResponse('not-halted')
export type FleetRenewalResumeResponse = z.infer<typeof FleetRenewalResumeResponseSchema>

/** `POST /fleet/renewals/read`: one renewal by id. */
export const FleetRenewalReadRequestSchema = z.object({ renewalId: RenewalIdSchema }).strict()
export type FleetRenewalReadRequest = z.infer<typeof FleetRenewalReadRequestSchema>

export const FleetRenewalReadResponseSchema = z.object({ renewal: FleetRenewalDocumentSchema.nullable() }).strict()
export type FleetRenewalReadResponse = z.infer<typeof FleetRenewalReadResponseSchema>

/**
 * `POST /fleet/renewals/summary`: the ledger half of the console card. The backend adds the flag and the
 * stored session's freshness to make `RenewalHealthSummary`.
 */
export const FleetRenewalSummaryRequestSchema = z
  .object({
    slug: SessionSlugSchema,
    /** `null` when the flag is absent or unreadable: no cooldown is computed. */
    cooldownMinutes: z.number().int().nonnegative().nullable(),
    recentLimit: z.number().int().positive().max(100)
  })
  .strict()
export type FleetRenewalSummaryRequest = z.infer<typeof FleetRenewalSummaryRequestSchema>

export const FleetRenewalSummaryResponseSchema = z
  .object({
    inFlight: FleetRenewalDocumentSchema.nullable(),
    halted: FleetRenewalDocumentSchema.nullable(),
    /** Newest `startedAt` first. */
    recent: z.array(FleetRenewalDocumentSchema),
    latestSucceeded: FleetRenewalDocumentSchema.nullable(),
    /** Today's (UTC) password submissions plus in-flight rows holding an unstamped reservation. */
    signInsUsed: z.number().int().nonnegative(),
    smsUsed: z.number().int().nonnegative(),
    capResetsAt: IsoTimestampSchema,
    cooldown: z
      .object({ until: IsoTimestampSchema, outcomeCode: RenewalRecordedCodeSchema.nullable() })
      .strict()
      .nullable()
  })
  .strict()
export type FleetRenewalSummaryResponse = z.infer<typeof FleetRenewalSummaryResponseSchema>

/** `POST /fleet/renewals/halt-state`: the HALTED row holding the slug's lock, if any. Read fail-closed (KTD8). */
export const FleetRenewalHaltStateRequestSchema = z.object({ slug: SessionSlugSchema }).strict()
export type FleetRenewalHaltStateRequest = z.infer<typeof FleetRenewalHaltStateRequestSchema>

export const FleetRenewalHaltStateResponseSchema = z.object({ halted: FleetRenewalDocumentSchema.nullable() }).strict()
export type FleetRenewalHaltStateResponse = z.infer<typeof FleetRenewalHaltStateResponseSchema>

// =============================================================================
// Provisioning runs and steps
// =============================================================================

/**
 * `POST /fleet/provisioning/runs/create`: a run and all its steps in one mutation (replacing the nested
 * Prisma create). The backend mints every public id. `schoolId` comes from `inputs.schoolId`.
 */
export const FleetRunCreateRequestSchema = z
  .object({
    publicId: PublicIdSchema,
    mobileAppPublicId: PublicIdSchema.nullable(),
    initiatedByUserPublicId: PublicIdSchema.nullable(),
    initiatorSource: FleetProvisioningInitiatorSourceSchema,
    inputs: FleetProvisioningRunInputsSchema,
    steps: z
      .array(
        z
          .object({
            publicId: PublicIdSchema,
            stepName: z.string().min(1).max(128),
            stepOrder: z.number().int(),
            /** SKIPPED on a browser-only run, whose API half is never attempted. */
            status: ProvisioningStepStatusSchema.extract(['PENDING', 'SKIPPED'])
          })
          .strict()
      )
      .min(1)
  })
  .strict()
export type FleetRunCreateRequest = z.infer<typeof FleetRunCreateRequestSchema>

/** `created: false` when the run id already existed (a retried create); the run is answered as it stands. */
export const FleetRunCreateResponseSchema = z
  .object({ created: z.boolean(), run: FleetProvisioningRunWithStepsSchema })
  .strict()
export type FleetRunCreateResponse = z.infer<typeof FleetRunCreateResponseSchema>

/** `POST /fleet/provisioning/runs/get`. */
export const FleetRunGetRequestSchema = z.object({ runPublicId: PublicIdSchema }).strict()
export type FleetRunGetRequest = z.infer<typeof FleetRunGetRequestSchema>

export const FleetRunGetResponseSchema = z.object({ run: FleetProvisioningRunWithStepsSchema.nullable() }).strict()
export type FleetRunGetResponse = z.infer<typeof FleetRunGetResponseSchema>

/** `POST /fleet/provisioning/runs/list`: newest first, optionally one app's runs; `total` honours the filter. */
export const FleetRunListRequestSchema = z
  .object({
    limit: z.number().int().positive().max(200),
    offset: z.number().int().nonnegative(),
    mobileAppPublicId: PublicIdSchema.optional()
  })
  .strict()
export type FleetRunListRequest = z.infer<typeof FleetRunListRequestSchema>

export const FleetRunListResponseSchema = z
  .object({ runs: z.array(FleetProvisioningRunWithStepsSchema), total: z.number().int().nonnegative() })
  .strict()
export type FleetRunListResponse = z.infer<typeof FleetRunListResponseSchema>

/** `POST /fleet/provisioning/runs/latest-for-apps`: each named app's newest run (`readAppProvisioning`). */
export const FleetRunLatestForAppsRequestSchema = z.object({ mobileAppPublicIds: IdListSchema }).strict()
export type FleetRunLatestForAppsRequest = z.infer<typeof FleetRunLatestForAppsRequestSchema>

/** Apps with no run are absent. */
export const FleetRunLatestForAppsResponseSchema = z
  .object({ runs: z.array(FleetProvisioningRunWithStepsSchema) })
  .strict()
export type FleetRunLatestForAppsResponse = z.infer<typeof FleetRunLatestForAppsResponseSchema>

/** `POST /fleet/provisioning/runs/attach-app`: set `mobileAppPublicId` on the run and every step (KTD3). */
export const FleetRunAttachAppRequestSchema = z
  .object({ runPublicId: PublicIdSchema, mobileAppPublicId: PublicIdSchema })
  .strict()
export type FleetRunAttachAppRequest = z.infer<typeof FleetRunAttachAppRequestSchema>

/** `conflict` when the run already names a DIFFERENT app; re-attaching the same app is `applied`. */
export const FleetRunAttachAppResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('applied'), run: FleetProvisioningRunWithStepsSchema }).strict(),
  z.object({ kind: z.literal('conflict'), run: FleetProvisioningRunWithStepsSchema }).strict(),
  z.object({ kind: z.literal('not-found') }).strict()
])
export type FleetRunAttachAppResponse = z.infer<typeof FleetRunAttachAppResponseSchema>

/**
 * `POST /fleet/provisioning/steps/claim`: PENDING → IN_PROGRESS (start stamped, attempt counted, error
 * cleared) and the run bumped to IN_PROGRESS, in one mutation. Exactly one of two concurrent claims wins.
 */
export const FleetStepClaimRequestSchema = z
  .object({ runPublicId: PublicIdSchema, stepPublicId: PublicIdSchema })
  .strict()
export type FleetStepClaimRequest = z.infer<typeof FleetStepClaimRequestSchema>

/** `taken`: the step was not PENDING; `step` is what holds it now. */
export const FleetStepClaimResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('claimed'), run: FleetProvisioningRunWithStepsSchema }).strict(),
  z.object({ kind: z.literal('taken'), step: FleetProvisioningStepDocumentSchema }).strict(),
  z.object({ kind: z.literal('not-found') }).strict()
])
export type FleetStepClaimResponse = z.infer<typeof FleetStepClaimResponseSchema>

/**
 * `POST /fleet/provisioning/steps/settle`: a step's move and its run's move in one mutation (replacing
 * the `$transaction` pairs: outcome, stale recovery, retry, manual completion, finalize). Either half
 * may be absent, not both. The step half is guarded: it applies only from a status in `from`.
 * Absent fields are left alone; `null` clears.
 */
export const FleetStepSettleRequestSchema = z
  .object({
    runPublicId: PublicIdSchema,
    step: z
      .object({
        stepPublicId: PublicIdSchema,
        from: z.array(ProvisioningStepStatusSchema).min(1),
        to: ProvisioningStepStatusSchema,
        output: z.unknown().optional(),
        error: z.unknown().optional(),
        startedAt: FleetTimestampWriteSchema.optional(),
        completedAt: FleetTimestampWriteSchema.optional()
      })
      .strict()
      .optional(),
    run: z
      .object({
        status: ProvisioningRunStatusSchema,
        error: z.unknown().optional(),
        completedAt: FleetTimestampWriteSchema.optional()
      })
      .strict()
      .optional()
  })
  .strict()
  .refine(r => r.step !== undefined || r.run !== undefined, { message: 'settle moves a step, a run, or both' })
export type FleetStepSettleRequest = z.infer<typeof FleetStepSettleRequestSchema>

/** `step-moved`: the step was no longer in `from`; nothing was written, `step` is how it stands. */
export const FleetStepSettleResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('applied'), run: FleetProvisioningRunWithStepsSchema }).strict(),
  z.object({ kind: z.literal('step-moved'), step: FleetProvisioningStepDocumentSchema }).strict(),
  z.object({ kind: z.literal('not-found') }).strict()
])
export type FleetStepSettleResponse = z.infer<typeof FleetStepSettleResponseSchema>

// =============================================================================
// Provisioning dispatches
// =============================================================================

/** A dispatch created already settled, for work that finished before it was recorded. */
const FinishedDispatchSchema = z
  .object({
    status: ProvisioningDispatchStatusSchema.extract(['SUCCEEDED', 'PARTIAL', 'FAILED', 'TIMED_OUT']),
    startedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema,
    error: FleetErrorSchema.optional()
  })
  .strict()

const dispatchCreateFields = {
  publicId: PublicIdSchema,
  stepIds: z.array(z.string().min(1)).min(1),
  sessionSlug: SessionSlugSchema,
  stepStates: FleetStepStatesSchema,
  requestedByUserPublicId: PublicIdSchema.nullable(),
  finished: FinishedDispatchSchema.optional()
}

/**
 * `POST /fleet/provisioning/dispatches/create`.
 * - `scope: 'run'`: `schoolId` is copied from the run. With `guardInFlight`, the mutation refuses when a
 *   non-terminal dispatch of the same run holds the same `sessionSlug` (the dispatcher's drop-slug case).
 * - `scope: 'app'`: app-scoped (Android setup, every setup-graph record); never guarded. `schoolId` is
 *   required: the app's school, or an explicit `null` for an app with no school (the shared app).
 */
export const FleetDispatchCreateRequestSchema = z.discriminatedUnion('scope', [
  z
    .object({
      scope: z.literal('run'),
      runPublicId: PublicIdSchema,
      guardInFlight: z.boolean(),
      ...dispatchCreateFields
    })
    .strict(),
  z
    .object({
      scope: z.literal('app'),
      mobileAppPublicId: PublicIdSchema,
      schoolId: SchoolIdSchema.nullable(),
      ...dispatchCreateFields
    })
    .strict()
])
export type FleetDispatchCreateRequest = z.infer<typeof FleetDispatchCreateRequestSchema>

export const FleetDispatchCreateResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('created'), dispatch: FleetProvisioningDispatchDocumentSchema }).strict(),
  /** The guarded create found this dispatch holding the slug; nothing was inserted. */
  z.object({ kind: z.literal('already-in-flight'), dispatch: FleetProvisioningDispatchDocumentSchema }).strict(),
  z.object({ kind: z.literal('run-not-found') }).strict()
])
export type FleetDispatchCreateResponse = z.infer<typeof FleetDispatchCreateResponseSchema>

/**
 * `POST /fleet/provisioning/dispatches/update`: `DispatchStore.update`'s partial patch, for writers other
 * than the runner's report (the dispatcher's own status moves). `stepStates` here replaces the whole map.
 * Absent fields are left alone; `null` clears `error` or `completedAt`.
 */
export const FleetDispatchUpdateRequestSchema = z
  .object({
    publicId: PublicIdSchema,
    patch: z
      .object({
        status: ProvisioningDispatchStatusSchema.optional(),
        stepStates: FleetStepStatesSchema.optional(),
        error: FleetErrorSchema.nullable().optional(),
        startedAt: IsoTimestampSchema.optional(),
        completedAt: IsoTimestampSchema.nullable().optional()
      })
      .strict()
  })
  .strict()
export type FleetDispatchUpdateRequest = z.infer<typeof FleetDispatchUpdateRequestSchema>

export const FleetDispatchUpdateResponseSchema = z
  .object({ dispatch: FleetProvisioningDispatchDocumentSchema.nullable() })
  .strict()
export type FleetDispatchUpdateResponse = z.infer<typeof FleetDispatchUpdateResponseSchema>

/**
 * `POST /fleet/provisioning/dispatches/report`: one runner report, merged server-side (KTD5) and safe to
 * retry (KTD8). A `step` report merges that one step's entry and never overwrites another's; `finished`
 * carries the status the backend rolled up from the dispatch's own steps.
 */
export const FleetDispatchReportRequestSchema = z
  .object({
    dispatchPublicId: PublicIdSchema,
    report: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('started') }).strict(),
      z
        .object({ kind: z.literal('step'), stepId: z.string().min(1), state: BrowserStepStateSchema.passthrough() })
        .strict(),
      z
        .object({
          kind: z.literal('finished'),
          status: ProvisioningDispatchStatusSchema.extract(['SUCCEEDED', 'PARTIAL', 'FAILED']),
          error: FleetErrorSchema.optional()
        })
        .strict()
    ])
  })
  .strict()
export type FleetDispatchReportRequest = z.infer<typeof FleetDispatchReportRequestSchema>

/**
 * `applied`: the report changed the dispatch. `duplicate`: it was already reflected (a retry), nothing
 * changed. `refused`: a `started` or `finished` report on a dispatch already terminal (a late finish on a
 * TIMED_OUT dispatch leaves it TIMED_OUT); a late `step` report still lands, for forensics.
 */
export const FleetDispatchReportResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('applied'), dispatch: FleetProvisioningDispatchDocumentSchema }).strict(),
  z.object({ kind: z.literal('duplicate'), dispatch: FleetProvisioningDispatchDocumentSchema }).strict(),
  z
    .object({
      kind: z.literal('refused'),
      reason: z.literal('terminal'),
      dispatch: FleetProvisioningDispatchDocumentSchema
    })
    .strict(),
  z.object({ kind: z.literal('not-found') }).strict()
])
export type FleetDispatchReportResponse = z.infer<typeof FleetDispatchReportResponseSchema>

/** `POST /fleet/provisioning/dispatches/renew-lease`: extend the heartbeat lease from the store's clock. */
export const FleetDispatchRenewLeaseRequestSchema = z
  .object({ publicId: PublicIdSchema, leaseMs: DurationMsSchema })
  .strict()
export type FleetDispatchRenewLeaseRequest = z.infer<typeof FleetDispatchRenewLeaseRequestSchema>

/** `held: false` once the dispatch is gone or already terminal: the runner stops. Does not touch `updatedAt`. */
export const FleetDispatchRenewLeaseResponseSchema = z
  .object({ held: z.boolean(), leaseExpiresAt: IsoTimestampSchema.nullable() })
  .strict()
export type FleetDispatchRenewLeaseResponse = z.infer<typeof FleetDispatchRenewLeaseResponseSchema>

/**
 * `POST /fleet/provisioning/dispatches/sweep-stale`: mark non-terminal dispatches TIMED_OUT — a leased
 * one once its lease expired, an unleased one once `updatedAt` is older than `staleAfterMs` (45 minutes
 * today) — optionally within one run.
 */
export const FleetDispatchSweepStaleRequestSchema = z
  .object({ staleAfterMs: DurationMsSchema, runPublicId: PublicIdSchema.optional() })
  .strict()
export type FleetDispatchSweepStaleRequest = z.infer<typeof FleetDispatchSweepStaleRequestSchema>

export const FleetDispatchSweepStaleResponseSchema = z.object({ swept: z.number().int().nonnegative() }).strict()
export type FleetDispatchSweepStaleResponse = z.infer<typeof FleetDispatchSweepStaleResponseSchema>

/**
 * `POST /fleet/provisioning/dispatches/run-record-cas`: the setup-graph run record's compare-and-set
 * (`mutateRunRecord`). Applies only while `updatedAt` still equals `expectedUpdatedAt`, and stamps a
 * strictly later one. `stepStates` replaces the whole map. Absent fields are left alone; `null` clears
 * `completedAt` or `error`.
 */
export const FleetDispatchRunRecordCasRequestSchema = z
  .object({
    publicId: PublicIdSchema,
    expectedUpdatedAt: IsoTimestampSchema,
    change: z
      .object({
        stepStates: FleetStepStatesSchema.optional(),
        status: ProvisioningDispatchStatusSchema.optional(),
        completedAt: IsoTimestampSchema.nullable().optional(),
        error: FleetErrorSchema.nullable().optional()
      })
      .strict()
  })
  .strict()
export type FleetDispatchRunRecordCasRequest = z.infer<typeof FleetDispatchRunRecordCasRequestSchema>

/** `stale`: another writer moved the record; `dispatch` is its current state, to decide again from. */
export const FleetDispatchRunRecordCasResponseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('applied'), dispatch: FleetProvisioningDispatchDocumentSchema }).strict(),
  z.object({ kind: z.literal('stale'), dispatch: FleetProvisioningDispatchDocumentSchema }).strict(),
  z.object({ kind: z.literal('not-found') }).strict()
])
export type FleetDispatchRunRecordCasResponse = z.infer<typeof FleetDispatchRunRecordCasResponseSchema>

/** `POST /fleet/provisioning/dispatches/get`. */
export const FleetDispatchGetRequestSchema = z.object({ publicId: PublicIdSchema }).strict()
export type FleetDispatchGetRequest = z.infer<typeof FleetDispatchGetRequestSchema>

export const FleetDispatchGetResponseSchema = z
  .object({ dispatch: FleetProvisioningDispatchDocumentSchema.nullable() })
  .strict()
export type FleetDispatchGetResponse = z.infer<typeof FleetDispatchGetResponseSchema>

/** `POST /fleet/provisioning/dispatches/list-for-runs`: every dispatch of the named runs, each run's oldest first. */
export const FleetDispatchListForRunsRequestSchema = z.object({ runPublicIds: IdListSchema }).strict()
export type FleetDispatchListForRunsRequest = z.infer<typeof FleetDispatchListForRunsRequestSchema>

export const FleetDispatchListResponseSchema = z
  .object({ dispatches: z.array(FleetProvisioningDispatchDocumentSchema) })
  .strict()
export type FleetDispatchListResponse = z.infer<typeof FleetDispatchListResponseSchema>

/**
 * `POST /fleet/provisioning/dispatches/list-for-app`: one app's OWN (app-scoped) dispatches, newest
 * first, one page per call. Paged on the `(mobileAppPublicId, createdAt)` index so the graph observer's
 * window and older scan stay inside a function's read limits (KTD11).
 */
export const FleetDispatchListForAppRequestSchema = z
  .object({
    mobileAppPublicId: PublicIdSchema,
    limit: z.number().int().positive().max(FLEET_OPS_MAX_IDS),
    /** The previous page's `continueCursor`; `null` for the first page. */
    cursor: z.string().nullable()
  })
  .strict()
export type FleetDispatchListForAppRequest = z.infer<typeof FleetDispatchListForAppRequestSchema>

export const FleetDispatchPageResponseSchema = z
  .object({
    dispatches: z.array(FleetProvisioningDispatchDocumentSchema),
    /** `null` when there is no further page. */
    continueCursor: z.string().nullable()
  })
  .strict()
export type FleetDispatchPageResponse = z.infer<typeof FleetDispatchPageResponseSchema>

/**
 * `POST /fleet/provisioning/dispatches/list-by-session`: dispatches on one session slug in the given
 * statuses, for the setup drive's boot adoption (`listOpenRunRecords`).
 */
export const FleetDispatchListBySessionRequestSchema = z
  .object({
    sessionSlug: SessionSlugSchema,
    statuses: z.array(ProvisioningDispatchStatusSchema).min(1),
    limit: z.number().int().positive().max(FLEET_OPS_MAX_IDS)
  })
  .strict()
export type FleetDispatchListBySessionRequest = z.infer<typeof FleetDispatchListBySessionRequestSchema>

// =============================================================================
// Purge and verification digest
// =============================================================================

/**
 * `POST /fleet/purge`: delete one school's or one app's runs, steps and dispatches (app-scoped ones
 * included), in self-rescheduling batches (KTD9). Renewals carry no school and are never purged here.
 * A school target also deletes its AI artifacts with their content, grants and views, and the grants
 * expanded from its admin list on other schools' artifacts (AI artifacts plan U5, R19).
 */
export const FleetPurgeRequestSchema = z
  .object({
    target: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('school'), schoolId: SchoolIdSchema }).strict(),
      z.object({ kind: z.literal('app'), mobileAppPublicId: PublicIdSchema }).strict()
    ]),
    /** Count what would be deleted and delete nothing. */
    dryRun: z.boolean()
  })
  .strict()
export type FleetPurgeRequest = z.infer<typeof FleetPurgeRequestSchema>

/**
 * `counts`: the matching documents at call time. `pending`: a later batch is scheduled; the caller
 * re-runs a dry run until every count is zero before it touches PlanetScale. An app target reaches no
 * AI artifact, so its three artifact counts are zero. An artifact's member-write and anonymous-open rows
 * go before it and are not counted apart.
 */
export const FleetPurgeResponseSchema = z
  .object({
    dryRun: z.boolean(),
    counts: z
      .object({
        runs: z.number().int().nonnegative(),
        steps: z.number().int().nonnegative(),
        dispatches: z.number().int().nonnegative(),
        artifacts: z.number().int().nonnegative(),
        artifactGrants: z.number().int().nonnegative(),
        artifactViews: z.number().int().nonnegative()
      })
      .strict(),
    pending: z.boolean()
  })
  .strict()
export type FleetPurgeResponse = z.infer<typeof FleetPurgeResponseSchema>

/** A SHA-256 over a document's canonical JSON, lowercase hex. */
const DigestHashSchema = z.string().regex(/^[a-f0-9]{64}$/)

/**
 * `POST /fleet/digest`: one page of a table's verification digest — one canonical hash per public id
 * (`renewalId` for renewals). The caller pages to the end and sums the counts.
 */
export const FleetDigestRequestSchema = z
  .object({
    table: FleetOpsTableSchema,
    limit: z.number().int().positive().max(FLEET_OPS_MAX_IDS),
    cursor: z.string().nullable()
  })
  .strict()
export type FleetDigestRequest = z.infer<typeof FleetDigestRequestSchema>

export const FleetDigestResponseSchema = z
  .object({
    table: FleetOpsTableSchema,
    entries: z.array(z.object({ id: z.string().min(1), hash: DigestHashSchema }).strict()),
    /**
     * Run, step and dispatch documents on this page with no `schoolId`, other than the school-less app
     * dispatches counted below (always 0 for renewals).
     */
    missingSchoolId: z.number().int().nonnegative(),
    /** App-scoped dispatches on this page holding `null` for `schoolId`: their app has no school. */
    schoolLessAppDispatches: z.number().int().nonnegative(),
    continueCursor: z.string().nullable()
  })
  .strict()
export type FleetDigestResponse = z.infer<typeof FleetDigestResponseSchema>

/** `POST /fleet/digest/fetch`: the full documents for named ids, to diff field by field. */
export const FleetDigestFetchRequestSchema = z
  .object({ table: FleetOpsTableSchema, ids: z.array(z.string().min(1)).min(1).max(FLEET_OPS_MAX_IDS) })
  .strict()
export type FleetDigestFetchRequest = z.infer<typeof FleetDigestFetchRequestSchema>

/** Ids with no document are absent. */
export const FleetDigestFetchResponseSchema = z.discriminatedUnion('table', [
  z.object({ table: z.literal('renewals'), documents: z.array(FleetRenewalDocumentSchema) }).strict(),
  z.object({ table: z.literal('runs'), documents: z.array(FleetProvisioningRunDocumentSchema) }).strict(),
  z.object({ table: z.literal('steps'), documents: z.array(FleetProvisioningStepDocumentSchema) }).strict(),
  z.object({ table: z.literal('dispatches'), documents: z.array(FleetProvisioningDispatchDocumentSchema) }).strict()
])
export type FleetDigestFetchResponse = z.infer<typeof FleetDigestFetchResponseSchema>

// =============================================================================
// Routes
// =============================================================================

/** Every fleet-ops route. All are `POST` with a JSON body, under `authedHttpAction({ scope: 'fleetops:write' })`. */
export const FLEET_OPS_ROUTES = {
  renewals: {
    request: '/fleet/renewals/request',
    failRequested: '/fleet/renewals/fail-requested',
    claim: '/fleet/renewals/claim',
    admit: '/fleet/renewals/admit',
    release: '/fleet/renewals/release',
    progress: '/fleet/renewals/progress',
    authorizeSms: '/fleet/renewals/authorize-sms',
    operatorCode: '/fleet/renewals/operator-code',
    complete: '/fleet/renewals/complete',
    fail: '/fleet/renewals/fail',
    resume: '/fleet/renewals/resume',
    read: '/fleet/renewals/read',
    summary: '/fleet/renewals/summary',
    haltState: '/fleet/renewals/halt-state'
  },
  runs: {
    create: '/fleet/provisioning/runs/create',
    get: '/fleet/provisioning/runs/get',
    list: '/fleet/provisioning/runs/list',
    latestForApps: '/fleet/provisioning/runs/latest-for-apps',
    attachApp: '/fleet/provisioning/runs/attach-app'
  },
  steps: {
    claim: '/fleet/provisioning/steps/claim',
    settle: '/fleet/provisioning/steps/settle'
  },
  dispatches: {
    create: '/fleet/provisioning/dispatches/create',
    update: '/fleet/provisioning/dispatches/update',
    report: '/fleet/provisioning/dispatches/report',
    renewLease: '/fleet/provisioning/dispatches/renew-lease',
    sweepStale: '/fleet/provisioning/dispatches/sweep-stale',
    runRecordCas: '/fleet/provisioning/dispatches/run-record-cas',
    get: '/fleet/provisioning/dispatches/get',
    listForRuns: '/fleet/provisioning/dispatches/list-for-runs',
    listForApp: '/fleet/provisioning/dispatches/list-for-app',
    listBySession: '/fleet/provisioning/dispatches/list-by-session'
  },
  store: {
    purge: '/fleet/purge',
    digest: '/fleet/digest',
    digestFetch: '/fleet/digest/fetch'
  }
} as const

type RouteGroups = typeof FLEET_OPS_ROUTES
export type FleetOpsRoute = { [G in keyof RouteGroups]: RouteGroups[G][keyof RouteGroups[G]] }[keyof RouteGroups]

/** The request and response schema of every route, keyed by path. */
export const FLEET_OPS_ROUTE_CONTRACTS = {
  [FLEET_OPS_ROUTES.renewals.request]: {
    request: FleetRenewalRequestRequestSchema,
    response: FleetRenewalRequestResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.failRequested]: {
    request: FleetRenewalFailRequestedRequestSchema,
    response: FleetRenewalFailRequestedResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.claim]: {
    request: FleetRenewalClaimRequestSchema,
    response: FleetRenewalClaimResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.admit]: {
    request: FleetRenewalAdmitRequestSchema,
    response: FleetRenewalAdmitResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.release]: {
    request: FleetRenewalReleaseRequestSchema,
    response: FleetRenewalReleaseResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.progress]: {
    request: FleetRenewalProgressRequestSchema,
    response: FleetRenewalProgressResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.authorizeSms]: {
    request: FleetRenewalAuthorizeSmsRequestSchema,
    response: FleetRenewalAuthorizeSmsResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.operatorCode]: {
    request: FleetRenewalOperatorCodeRequestSchema,
    response: FleetRenewalOperatorCodeResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.complete]: {
    request: FleetRenewalCompleteRequestSchema,
    response: FleetRenewalCompleteResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.fail]: {
    request: FleetRenewalFailRequestSchema,
    response: FleetRenewalFailResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.resume]: {
    request: FleetRenewalResumeRequestSchema,
    response: FleetRenewalResumeResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.read]: {
    request: FleetRenewalReadRequestSchema,
    response: FleetRenewalReadResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.summary]: {
    request: FleetRenewalSummaryRequestSchema,
    response: FleetRenewalSummaryResponseSchema
  },
  [FLEET_OPS_ROUTES.renewals.haltState]: {
    request: FleetRenewalHaltStateRequestSchema,
    response: FleetRenewalHaltStateResponseSchema
  },
  [FLEET_OPS_ROUTES.runs.create]: { request: FleetRunCreateRequestSchema, response: FleetRunCreateResponseSchema },
  [FLEET_OPS_ROUTES.runs.get]: { request: FleetRunGetRequestSchema, response: FleetRunGetResponseSchema },
  [FLEET_OPS_ROUTES.runs.list]: { request: FleetRunListRequestSchema, response: FleetRunListResponseSchema },
  [FLEET_OPS_ROUTES.runs.latestForApps]: {
    request: FleetRunLatestForAppsRequestSchema,
    response: FleetRunLatestForAppsResponseSchema
  },
  [FLEET_OPS_ROUTES.runs.attachApp]: {
    request: FleetRunAttachAppRequestSchema,
    response: FleetRunAttachAppResponseSchema
  },
  [FLEET_OPS_ROUTES.steps.claim]: { request: FleetStepClaimRequestSchema, response: FleetStepClaimResponseSchema },
  [FLEET_OPS_ROUTES.steps.settle]: { request: FleetStepSettleRequestSchema, response: FleetStepSettleResponseSchema },
  [FLEET_OPS_ROUTES.dispatches.create]: {
    request: FleetDispatchCreateRequestSchema,
    response: FleetDispatchCreateResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.update]: {
    request: FleetDispatchUpdateRequestSchema,
    response: FleetDispatchUpdateResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.report]: {
    request: FleetDispatchReportRequestSchema,
    response: FleetDispatchReportResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.renewLease]: {
    request: FleetDispatchRenewLeaseRequestSchema,
    response: FleetDispatchRenewLeaseResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.sweepStale]: {
    request: FleetDispatchSweepStaleRequestSchema,
    response: FleetDispatchSweepStaleResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.runRecordCas]: {
    request: FleetDispatchRunRecordCasRequestSchema,
    response: FleetDispatchRunRecordCasResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.get]: {
    request: FleetDispatchGetRequestSchema,
    response: FleetDispatchGetResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.listForRuns]: {
    request: FleetDispatchListForRunsRequestSchema,
    response: FleetDispatchListResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.listForApp]: {
    request: FleetDispatchListForAppRequestSchema,
    response: FleetDispatchPageResponseSchema
  },
  [FLEET_OPS_ROUTES.dispatches.listBySession]: {
    request: FleetDispatchListBySessionRequestSchema,
    response: FleetDispatchListResponseSchema
  },
  [FLEET_OPS_ROUTES.store.purge]: { request: FleetPurgeRequestSchema, response: FleetPurgeResponseSchema },
  [FLEET_OPS_ROUTES.store.digest]: { request: FleetDigestRequestSchema, response: FleetDigestResponseSchema },
  [FLEET_OPS_ROUTES.store.digestFetch]: {
    request: FleetDigestFetchRequestSchema,
    response: FleetDigestFetchResponseSchema
  }
} as const satisfies Record<FleetOpsRoute, { request: z.ZodType; response: z.ZodType }>
